import "./DueNow.scss";

import { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { getCurrency } from "@/utils/translationHelpers";

export type DueNowProps = {
  totalOnce: number;
  subtotalOnce: number;
  discountAmount?: number;
  /** Named basket discount (e.g. "B2B 10%"); falls back to generic "Rabatt". */
  discountName?: string;
  discountEndDate?: Date;
  currency: string;
  dataRateName?: string;
  dataRatePrice?: number;
  billingPeriod: string;
  recurringCaption?: string;
  recurringAmount?: number;
  recurringDate?: string;
  recurringBillingPeriod?: string;
  voiceOptionName?: string | ReactNode;
  voiceOptionPrice?: number;
  watchSimName?: string;
  watchSimPrice?: number;
  credit?: number;
  creditTimeRemaining?: number;
  icon?: ReactNode;
  isPrecharged?: boolean;
  hideSubtotal?: boolean;
};

// TODO - Refactor the RoamingDueNow DeviceTile so they make more sense,
// and the DueNowLightInterface should be reused and renamed
export const DueNow = ({
  totalOnce,
  subtotalOnce,
  discountAmount,
  discountName,
  discountEndDate,
  currency,
  isPrecharged,
  billingPeriod,
  recurringCaption,
  recurringAmount,
  recurringDate,
  recurringBillingPeriod,
  dataRateName,
  dataRatePrice,
  voiceOptionName,
  voiceOptionPrice,
  watchSimName,
  watchSimPrice,
  credit,
  creditTimeRemaining,
  icon,
  hideSubtotal,
}: DueNowProps) => {
  const { t } = useTranslation();

  const hasCredit = !!credit && credit > 0;
  const hasCreditTimeRemaining =
    !!creditTimeRemaining && creditTimeRemaining > 0;
  const hasDiscount = !!discountAmount && discountAmount > 0;

  const shouldShowSubtotalSection = hideSubtotal
    ? hasCredit || hasCreditTimeRemaining || hasDiscount
    : !!subtotalOnce &&
      subtotalOnce > 0 &&
      (hasCredit ||
        hasCreditTimeRemaining ||
        hasDiscount ||
        subtotalOnce > totalOnce) &&
      (!isPrecharged || hasCredit || hasCreditTimeRemaining || hasDiscount);

  return (
    <ProfileCard
      variant="white"
      className="payment-box-wrapper w-[calc(100vw-2rem)] xsPlus:w-full"
      title={
        <Trans components={[<span key="dueNow" />]} t={t}>
          Onboarding.sections.checkout.summary.payOnce
        </Trans>
      }
      actions={icon && <div id="device-icon">{icon}</div>}
    >
      <div className="payment-breakdown">
        {!!dataRateName &&
          dataRatePrice !== undefined &&
          dataRatePrice >= 0 && (
            // Design highlights only this first (the main plan) row with a
            // backdrop - its own mini grid inside one backgrounded, rounded
            // wrapper, same as e.g. Summary's TariffFeatureGroup boxes. Just
            // name + price here - unlike the rows below, the design doesn't
            // show a billing-period column for this one.
            <div className="flat-price-highlight">
              <div className="flat-name">{dataRateName}</div>
              <span className="price" data-testid="flat-price">
                {getCurrency(dataRatePrice, currency)}
              </span>
            </div>
          )}
        {/* Exclude Voice Option as it is already included in 'Flat Mobile' */}
        {!dataRateName?.startsWith("Flat Mobile") &&
          !!voiceOptionName &&
          voiceOptionPrice !== undefined &&
          voiceOptionPrice >= 0 && (
            <>
              <div className="flat-name">
                {voiceOptionName}
                <p className="mobile-days-label">{billingPeriod}</p>
              </div>
              <span className="days-label">{billingPeriod}</span>
              <span className="price" data-testid="voice-price">
                {getCurrency(voiceOptionPrice, currency)}
              </span>
            </>
          )}
        {!!watchSimName &&
          watchSimPrice !== undefined &&
          watchSimPrice >= 0 && (
            <>
              <div className="flat-name">
                {watchSimName}
                <p className="mobile-days-label">{billingPeriod}</p>
              </div>
              <span className="days-label">{billingPeriod}</span>
              <span className="price" data-testid="watch-price">
                {getCurrency(watchSimPrice, currency)}
              </span>
            </>
          )}
      </div>
      {shouldShowSubtotalSection && (
        <>
          <div className="total-breakdown">
            {!hideSubtotal && (
              <>
                <div className="subtotal">
                  <span>
                    {t("Onboarding.sections.checkout.summary.subtotal")}
                  </span>
                </div>
                <div className="subtotal-amount">
                  {getCurrency(subtotalOnce, currency)}
                </div>
              </>
            )}
            {hasCreditTimeRemaining && (
              <>
                <span className="credit">
                  {t("Onboarding.sections.checkout.summary.credit")}{" "}
                  <span className="hint">
                    {t("cockpit.managementTile.tariff.checkout.creditText")}
                  </span>
                </span>
                <span className="credit-amount">
                  - {getCurrency(creditTimeRemaining, currency)}
                </span>
              </>
            )}
            {hasDiscount && (
              <>
                <div className="subtotal">
                  <span>
                    {discountName ??
                      t("Onboarding.sections.checkout.summary.discount")}{" "}
                  </span>
                  {discountEndDate && (
                    <span className="hint">
                      <Trans
                        t={t}
                        i18nKey="Onboarding.sections.checkout.summary.until_date"
                        components={[
                          <span key="until_date" className="text-red-500" />,
                        ]}
                        values={{
                          date: discountEndDate.toLocaleDateString("de-DE"),
                        }}
                      />
                    </span>
                  )}
                </div>
                <div className="subtotal-amount">
                  <span className="rounded-full bg-surface-accent-mint px-2 py-1 text-body-xs font-semibold tracking-[0.24px] text-foreground-primary">
                    - {getCurrency(discountAmount, currency)}
                  </span>
                </div>
              </>
            )}
            {hasCredit && (
              <>
                <span className="credit">
                  {t("Onboarding.sections.checkout.summary.credit")}{" "}
                  <span className="hint">
                    {t(
                      "cockpit.managementTile.tariff.checkout.creditTextOthers",
                    )}
                  </span>
                </span>
                <span className="credit-amount">
                  - {getCurrency(credit, currency)}
                </span>
              </>
            )}
          </div>
        </>
      )}
      <hr className="line" />
      <div className="total-breakdown">
        <div className="total">
          {t("Onboarding.sections.checkout.summary.total")}
          <span className="tax-hint font-regular">
            {t("Onboarding.sections.checkout.summary.taxIncluded")}
          </span>
        </div>
        <div className="total-amount" data-testid="total-amount-due-now">
          <p>
            {totalOnce <= 0
              ? `${currency} 0.00`
              : getCurrency(totalOnce, currency)}
          </p>
          {recurringCaption && (
            <p className="text-xs font-normal leading-md text-foreground-secondary">
              {recurringCaption}
            </p>
          )}
        </div>
      </div>
      {recurringAmount !== undefined && (
        <div className="mx-2 grid grid-cols-2 gap-4 rounded-md bg-surface-quaternary px-2 py-3 md:mx-4">
          <div className="flex flex-col gap-1 text-foreground-primary">
            <p className="text-body-base font-semibold leading-none">
              {t("Onboarding.sections.checkout.summary.recurring")}
            </p>
            {recurringDate && (
              <p className="text-body-xs leading-none text-foreground-secondary">
                {recurringDate}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 text-foreground-primary">
            <p className="text-body-base font-semibold leading-none">
              {getCurrency(recurringAmount, currency)}
            </p>
            <p className="text-body-xs leading-none text-foreground-secondary">
              {recurringBillingPeriod ?? billingPeriod}
            </p>
          </div>
        </div>
      )}
    </ProfileCard>
  );
};
